import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, BarChart3, Users, Activity, Calendar, 
  AlertTriangle, ArrowUp, Filter, Download, Lock, Plus,
  X, Check, Info, Loader2, ChevronDown, ArrowLeft, ChevronRight,
  FileText, Zap, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Impact labels
const IMPACT_LABELS = {
  0: 'No Change',
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Extreme'
};

// Cohort ID to label mapping
const COHORT_LABELS = {
  executive: 'Executive',
  governance_assurance: 'Governance & Assurance',
  project_delivery: 'Project Delivery',
  operations: 'Operations',
  digital_technology: 'Digital & Technology',
  people_culture: 'People, Culture & Safety',
  community_engagement: 'Community & Engagement',
  all_staff: 'All Staff',
  finance: 'Finance',
  customer_service: 'Customer Service',
  field_teams: 'Field Teams',
  leadership: 'Leadership Team'
};

// Helper to get month index - MUST be defined before use
const getMonthIndex = (monthStr) => {
  if (!monthStr) return -1;
  const months = ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08'];
  return months.indexOf(monthStr);
};

// Helper to format delivery method - MUST be defined before use
const formatDeliveryMethod = (method) => {
  const labels = {
    fyi_email: 'FYI Email',
    action_email: 'Action Email',
    team_huddle: 'Team Huddle',
    drop_in_session: 'Drop-in',
    elearning: 'eLearning',
    formal_training: 'Training',
    coaching: 'Coaching',
    workshop: 'Workshop',
    townhall: 'Town Hall',
    one_on_one: '1:1 Meeting'
  };
  return labels[method] || method;
};

// Helper to calculate impost from activity
const calculateImpost = (activity) => {
  const impostsMap = {
    fyi_email: 0.25,
    action_email: 0.5,
    team_huddle: 0.5,
    drop_in_session: 1.0,
    elearning: 1.0,
    formal_training: 1.75,
    coaching: 2.0,
    workshop: 1.5,
    townhall: 0.75,
    one_on_one: 1.0
  };
  
  let total = 0;
  (activity.delivery_methods || []).forEach(method => {
    total += impostsMap[method] || 0;
  });
  total += (activity.additional_touchpoints || 0) * 0.25;
  
  return total.toFixed(1);
};

// Get color based on load value
const getHeatmapColor = (value) => {
  if (value === null || value === undefined) return 'bg-slate-100 text-slate-400';
  if (value <= 2) return 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200';
  if (value <= 3) return 'bg-emerald-200 text-emerald-800 hover:bg-emerald-300';
  if (value <= 4) return 'bg-lime-200 text-lime-800 hover:bg-lime-300';
  if (value <= 5) return 'bg-yellow-200 text-yellow-800 hover:bg-yellow-300';
  if (value <= 6) return 'bg-amber-300 text-amber-900 hover:bg-amber-400';
  if (value <= 7) return 'bg-orange-400 text-white hover:bg-orange-500';
  return 'bg-red-500 text-white hover:bg-red-600';
};

const getTotalColor = (value) => {
  if (value <= 25) return 'bg-emerald-200 text-emerald-800';
  if (value <= 35) return 'bg-yellow-200 text-yellow-800';
  if (value <= 37) return 'bg-amber-300 text-amber-900';
  return 'bg-orange-400 text-orange-900';
};

const getImpactColor = (rating) => {
  if (rating <= 1) return 'bg-emerald-100 text-emerald-700';
  if (rating <= 2) return 'bg-lime-100 text-lime-700';
  if (rating <= 3) return 'bg-yellow-100 text-yellow-700';
  if (rating <= 4) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

// Legend component
const Legend = () => (
  <div className="flex items-center gap-4 text-xs">
    <span className="text-slate-500">Saturation:</span>
    <div className="flex items-center gap-1">
      <div className="flex">
        <div className="w-6 h-4 bg-slate-100 rounded-l flex items-center justify-center text-[10px] text-slate-400">-</div>
        <div className="w-6 h-4 bg-emerald-200 flex items-center justify-center text-[10px]">Low</div>
        <div className="w-6 h-4 bg-yellow-200"></div>
        <div className="w-6 h-4 bg-amber-300"></div>
        <div className="w-6 h-4 bg-orange-400"></div>
        <div className="w-6 h-4 bg-red-500 rounded-r flex items-center justify-center text-[10px] text-white">High</div>
      </div>
    </div>
  </div>
);

// Activity Detail Modal - Drill-down popup
const ActivityDetailModal = ({ isOpen, onClose, cell, programs }) => {
  if (!isOpen || !cell) return null;

  const { cohortId, cohortName, month, monthIndex, value } = cell;
  
  // Find activities that contribute to this cell
  const contributingActivities = [];
  programs.forEach(program => {
    (program.activities || []).forEach(activity => {
      if (activity.cohorts?.includes(cohortId)) {
        const startIdx = getMonthIndex(activity.start_month);
        const endIdx = getMonthIndex(activity.end_month);
        if (monthIndex >= startIdx && monthIndex <= endIdx) {
          contributingActivities.push({
            ...activity,
            programName: program.name,
            programColor: program.color
          });
        }
      }
    });
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="activity-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="activity-modal"
        >
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="font-semibold text-slate-900">{cohortName}</h3>
              <p className="text-sm text-slate-500">{month} - {value} impost units</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-modal-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto">
            <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
              Contributing Activities ({contributingActivities.length})
            </h4>
            
            {contributingActivities.length === 0 ? (
              <p className="text-slate-500 text-sm">No activities found for this cell.</p>
            ) : (
              <div className="space-y-3">
                {contributingActivities.map((activity, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`activity-item-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                        style={{ backgroundColor: activity.programColor }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900">{activity.name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getImpactColor(activity.impact_rating)}`}>
                            Impact: {activity.impact_rating}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {activity.programName} - {activity.start_month} to {activity.end_month}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                          <Zap className="h-3 w-3" />
                          <span>{activity.calculated_impost || calculateImpost(activity)} impost units</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {activity.delivery_methods?.map((method, i) => (
                            <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">
                              {formatDeliveryMethod(method)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Blurred Preview Overlay for non-subscribers
const BlurredOverlay = ({ onUpgrade }) => (
  <div className="absolute inset-0 z-40 flex items-center justify-center backdrop-blur-sm bg-white/50 rounded-xl">
    <div className="text-center p-6 bg-white/90 rounded-xl shadow-lg max-w-sm">
      <div className="w-14 h-14 bg-amber-100 rounded-full mx-auto mb-4 flex items-center justify-center">
        <Lock className="h-7 w-7 text-amber-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">Pro Feature</h3>
      <p className="text-sm text-slate-600 mb-4">
        Upgrade to unlock full access to Change Load analytics and drill-down insights.
      </p>
      <Button 
        onClick={onUpgrade}
        className="bg-amber-500 hover:bg-amber-600 text-slate-900"
        data-testid="upgrade-from-blur-btn"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        Upgrade to Pro
      </Button>
    </div>
  </div>
);

export default function ChangeLoadDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);
  
  // Check if user has access (paid tier)
  const hasAccess = user?.subscription_tier && ['pro', 'enterprise'].includes(user.subscription_tier);

  useEffect(() => {
    // Always load data - we'll show blurred preview for non-subscribers
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, configRes, programsRes] = await Promise.all([
        axios.get(`${API}/change-load/dashboard`),
        axios.get(`${API}/change-load/config`),
        axios.get(`${API}/change-load/programs`)
      ]);
      setData(dashboardRes.data);
      setConfig(configRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      console.error('Error loading change load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (cohortName, cohortId, month, monthIndex, value) => {
    if (!hasAccess) {
      toast.info('Upgrade to Pro to view activity details');
      return;
    }
    if (value === null || value === undefined) return;
    setSelectedCell({ cohortName, cohortId, month, monthIndex, value });
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6" data-testid="change-load-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-500 hover:text-slate-700"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Change Load & Saturation
            </h1>
            <p className="text-slate-500">
              {hasAccess ? 'Click on any heatmap cell to see contributing activities' : 'Preview mode - upgrade to interact with the data'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <p className="text-xs text-slate-500 mb-1">Understanding the metrics</p>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span><strong>Impact</strong> = Size of change</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-500 rounded"></div>
                <span><strong>Impost</strong> = Delivery burden</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span><strong>Saturation</strong> = Total burden/month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 relative">
          {!hasAccess && <BlurredOverlay onUpgrade={handleUpgrade} />}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            data-testid="summary-total-load"
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Total Impost Load
              </span>
              <TrendingUp className="h-5 w-5 text-slate-300" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1">
              {data?.summary?.totalLoad || 0}
            </div>
            <div className="text-sm text-slate-500">cognitive load units</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            data-testid="summary-programs"
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Programs
              </span>
              <BarChart3 className="h-5 w-5 text-slate-300" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1">
              {data?.summary?.activePrograms || 0}
            </div>
            <div className="text-sm text-slate-500">{data?.summary?.totalActivities || 0} activities</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            data-testid="summary-functions"
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Functions Impacted
              </span>
              <Users className="h-5 w-5 text-slate-300" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1">
              {data?.summary?.functionsImpacted || 0}
            </div>
            <div className="text-sm text-slate-500">enterprise functions</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            data-testid="summary-avg-load"
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Avg Monthly Load
              </span>
              <Activity className="h-5 w-5 text-slate-300" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1">
              {data?.summary?.avgMonthlyLoad || 0}
            </div>
            <div className="text-sm text-slate-500">units per month</div>
          </motion.div>
        </div>

        {/* Early Warning Indicators */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-8 relative"
        >
          {!hasAccess && <BlurredOverlay onUpgrade={handleUpgrade} />}
          <div className="flex items-center gap-2 mb-6">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-900">Early Warning Indicators</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-testid="warning-peak-month">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-red-500" />
                <span className="text-xs font-medium text-red-600 uppercase">Peak Load Month</span>
              </div>
              <div className="text-xl font-bold text-slate-900">{data?.warnings?.peakMonth?.month || '-'}</div>
              <div className="text-sm text-slate-500">{data?.warnings?.peakMonth?.value || 0} impost units</div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4" data-testid="warning-most-impacted">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-medium text-amber-600 uppercase">Most Impacted Function</span>
              </div>
              <div className="text-xl font-bold text-slate-900 truncate">{data?.warnings?.mostImpacted?.function || '-'}</div>
              <div className="text-sm text-slate-500">{data?.warnings?.mostImpacted?.value || 0} total units</div>
            </div>

            <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4" data-testid="warning-overlap">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUp className="h-4 w-4 text-yellow-600" />
                <span className="text-xs font-medium text-yellow-700 uppercase">Max Program Overlap</span>
              </div>
              <div className="text-xl font-bold text-slate-900">{data?.warnings?.maxOverlap?.programs || 0} programs</div>
              <div className="text-sm text-slate-500 truncate">in {data?.warnings?.maxOverlap?.cohort || '-'}</div>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-lg p-4" data-testid="warning-consecutive">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-xs font-medium text-red-600 uppercase">Consecutive High-Load</span>
              </div>
              <div className="text-xl font-bold text-slate-900">{data?.warnings?.consecutiveHigh?.months || 0} months</div>
              <div className="text-sm text-slate-500">{data?.warnings?.consecutiveHigh?.note || ''}</div>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Saturation Heatmap */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-3 bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative"
          >
            {!hasAccess && <BlurredOverlay onUpgrade={handleUpgrade} />}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Saturation Heatmap</h2>
                <p className="text-sm text-slate-500">Click any cell to see contributing activities</p>
              </div>
              <Legend />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="heatmap-table">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-500 pb-3 pr-4 w-48"></th>
                    {months.map((month) => (
                      <th key={month} className="text-center text-xs font-medium text-slate-500 pb-3 px-1 w-14">
                        {month}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.heatmap?.functions?.map((func) => {
                    const cohortId = Object.entries(COHORT_LABELS).find(([id, label]) => label === func.name)?.[0] || func.name.toLowerCase().replace(/[^a-z]/g, '_');
                    
                    return (
                      <tr key={func.name}>
                        <td className="text-sm text-slate-700 py-1 pr-4 truncate max-w-[180px]">{func.name}</td>
                        {func.values.map((value, i) => (
                          <td key={i} className="p-1">
                            <button
                              onClick={() => handleCellClick(func.name, cohortId, `${months[i]} 2026`, i, value)}
                              className={`w-12 h-10 rounded flex items-center justify-center text-sm font-medium transition-all ${hasAccess ? 'cursor-pointer' : 'cursor-default'} ${getHeatmapColor(value)}`}
                              disabled={value === null || value === undefined}
                              data-testid={`heatmap-cell-${func.name}-${months[i]}`}
                            >
                              {value ?? '-'}
                            </button>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr className="border-t border-slate-200">
                    <td className="text-sm font-medium text-slate-700 py-2 pr-4">Monthly Total</td>
                    {data?.heatmap?.monthlyTotals?.map((total, i) => (
                      <td key={i} className="p-1">
                        <div className={`w-12 h-10 rounded flex items-center justify-center text-sm font-medium ${getTotalColor(total)}`}>
                          {total}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Program Contribution */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative"
          >
            {!hasAccess && <BlurredOverlay onUpgrade={handleUpgrade} />}
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Program Contribution</h2>
            
            {/* Stacked bar */}
            <div className="h-6 rounded-full overflow-hidden flex mb-6">
              {data?.programs?.map((program) => (
                <div 
                  key={program.name}
                  style={{ width: `${program.percentage}%`, backgroundColor: program.color }}
                  className="h-full"
                  title={`${program.name}: ${program.percentage}%`}
                />
              ))}
            </div>

            {/* Program list */}
            <div className="space-y-4">
              {data?.programs?.map((program) => (
                <div key={program.name} className="flex items-start gap-3" data-testid={`program-contrib-${program.name}`}>
                  <div 
                    className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                    style={{ backgroundColor: program.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900 truncate">{program.name}</span>
                      <span className="text-sm font-bold text-slate-900 ml-2">{program.load}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {program.activities} activities - {program.functions} functions - {program.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Total Impost Load</span>
                <span className="text-lg font-bold text-slate-900">{data?.summary?.totalLoad || 0} units</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Programs & Activities Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative"
        >
          {!hasAccess && <BlurredOverlay onUpgrade={handleUpgrade} />}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Programs & Activities</h2>
              <p className="text-sm text-slate-500">All change initiatives contributing to the load</p>
            </div>
          </div>
          
          <div className="divide-y divide-slate-100">
            {programs.map((program) => (
              <div key={program.id || program.name} className="bg-white" data-testid={`program-${program.id || program.name}`}>
                {/* Program Header */}
                <button
                  onClick={() => hasAccess && setExpandedProgram(expandedProgram === program.id ? null : program.id)}
                  className={`w-full px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${!hasAccess ? 'cursor-default' : ''}`}
                >
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: program.color }}
                  />
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-slate-900">{program.name}</h3>
                    <p className="text-sm text-slate-500">{program.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-slate-500">
                    <span>{program.activities?.length || 0} activities</span>
                    {hasAccess && <ChevronDown className={`h-5 w-5 transition-transform ${expandedProgram === program.id ? 'rotate-180' : ''}`} />}
                  </div>
                </button>
                
                {/* Activities List */}
                <AnimatePresence>
                  {expandedProgram === program.id && hasAccess && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 space-y-3">
                        {(program.activities || []).map((activity, idx) => (
                          <div 
                            key={activity.id || idx}
                            className="ml-8 p-4 bg-slate-50 rounded-lg border border-slate-100"
                            data-testid={`activity-${activity.id || idx}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-slate-900">{activity.name}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getImpactColor(activity.impact_rating)}`}>
                                Impact: {activity.impact_rating} ({IMPACT_LABELS[activity.impact_rating]})
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {activity.start_month} to {activity.end_month}
                              </span>
                              <span className="flex items-center gap-1">
                                <Zap className="h-4 w-4" />
                                {activity.calculated_impost || calculateImpost(activity)} impost units
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className="text-xs text-slate-500">Delivery:</span>
                              {activity.delivery_methods?.map((method, i) => (
                                <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">
                                  {formatDeliveryMethod(method)}
                                </span>
                              ))}
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-slate-500">Cohorts:</span>
                              {activity.cohorts?.map((cohort, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
                                  {COHORT_LABELS[cohort] || cohort}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Activity Detail Modal */}
        <ActivityDetailModal
          isOpen={!!selectedCell}
          onClose={() => setSelectedCell(null)}
          cell={selectedCell}
          programs={programs}
        />
      </div>
    </div>
  );
}
